import { Box, IconButton, TextField, Typography, Switch, FormControlLabel } from "@mui/material";
import { Modal, ModalBody } from "reactstrap";
import CloseIcon from "@mui/icons-material/Close";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { UsernameSelectField, styles } from "../../pages/MyTracks/styles";
import { useStyles } from "../../pages/MyTracks/styles";
import {
  GetEnterpriseAllUsersResponse,
  PostFolderResponse,
} from "../../proto/api_pb";
import { Collaborator, Folder, User } from "../../proto/api_pb";
import { useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { UserDetailsContext } from "../../contexts";
import { UserDetailsContextInterface } from "../../contexts";
import useGetValidAccessToken from "../../hooks/useGetValidAccessToken";
import { collaboratorsModalStyles, shareModalStyles } from "./styles";
import {
  APP_BACKGROUND_COLOR,
  APP_COLOR,
  BORDER_COLOR,
  FONT_FAMILY,
  TEXT_COLOR,
} from "../../theme";
import { getEnterpriseAllUsers } from "../../services/enterpriseService";
import { postFolder } from "../../services/folderService";
import CustomButton from "../CustomButton/CustomButton";

interface BatchEditFolderModalProps {
  open: boolean;
  onClose: () => void;
  selectedFolderIds: Set<string>;
  folders: Folder[];
  onSuccess: () => void;
}

const BatchEditFolderModal: React.FC<BatchEditFolderModalProps> = ({
  open,
  onClose,
  selectedFolderIds,
  folders,
  onSuccess,
}) => {
  const classes = useStyles();
  const [collabVal, setCollabVal] = useState("");
  const [usersList, setUsersList] = useState<Array<User>>([]);
  const { userName } = useContext<UserDetailsContextInterface>(UserDetailsContext);
  const [collaborators, setCollaborators] = useState<Array<Collaborator>>([]);
  const { getValidAccessToken } = useGetValidAccessToken();
  const [loading, setLoading] = useState<boolean>(false);
  const [userNames, setUserNames] = useState<string[]>([]);
  const { enterprise } = useContext(UserDetailsContext);

  const [privacyEnabled, setPrivacyEnabled] = useState<boolean>(false);
  const [makePrivate, setMakePrivate] = useState<boolean>(true);

  useEffect(() => {
    if (open) {
      setCollaborators([]);
      setCollabVal("");
      setPrivacyEnabled(false);
      setMakePrivate(true);
    }
  }, [open]);

  const addCollaborator = (newCollaborator: string) => {
    if (newCollaborator) {
      if (
        collaborators.find(
          (collaborator) => collaborator.getUsername() === newCollaborator
        ) === undefined &&
        usersList.some((user) => user.getUsername() === newCollaborator)
      ) {
        const addedCollab = usersList.find(
          (user) => user.getUsername() === newCollaborator
        );

        if (addedCollab) {
          const newCollab = new Collaborator();
          newCollab.setUserId(addedCollab?.getUserId());
          newCollab.setUsername(addedCollab?.getUsername());
          newCollab.setProfilePicture(addedCollab.getProfilePicture());
          setCollaborators([...collaborators, newCollab]);
        }
        setCollabVal("");
      } else {
        toast.info("Username does not exist or already added");
      }
    }
  };

  const removeCollaborator = (collaborator: Collaborator) => {
    const updatedCollaborators = collaborators.filter(
      (item) => item.getUserId() !== collaborator.getUserId()
    );
    setCollaborators(updatedCollaborators);
  };

  const handleConfirm = async () => {
    const hasCollabChanges = collaborators.length > 0;
    const hasPrivacyChanges = privacyEnabled;

    if (!hasCollabChanges && !hasPrivacyChanges) {
      toast.error("Please make at least one change");
      return;
    }
    if (selectedFolderIds.size === 0) {
      toast.error("No folders selected");
      return;
    }

    try {
      setLoading(true);
      const token = await getValidAccessToken();
      const folderIds = Array.from(selectedFolderIds);
      let failures = 0;

      for (const fId of folderIds) {
        const folder = folders.find((f) => f.getId() === fId);
        if (!folder) {
          failures++;
          continue;
        }
        try {
          if (hasCollabChanges) {
            const existing = folder.getCollaboratorsList();
            const existingIds = new Set(existing.map((c) => c.getUserId()));
            const newCollabs = collaborators.filter(
              (c) => !existingIds.has(c.getUserId())
            );
            folder.setCollaboratorsList([...existing, ...newCollabs]);
          }

          if (hasPrivacyChanges) {
            folder.setPrivate(makePrivate);
          }

          const res = await postFolder(token, folder);
          if (!(res instanceof PostFolderResponse)) {
            failures++;
          }
        } catch (error) {
          console.error(`Failed to update folder ${fId}:`, error);
          failures++;
        }
      }

      if (failures === 0) {
        toast.success(
          `Successfully updated ${folderIds.length} folder${folderIds.length !== 1 ? "s" : ""}`
        );
      } else {
        toast.warn(
          `Updated ${folderIds.length - failures} of ${folderIds.length} folders. ${failures} failed.`
        );
      }
      onSuccess();
      onClose();
    } catch (error) {
      console.error(error);
      toast.error("An error occurred");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleUserNames = async () => {
      const token = await getValidAccessToken();
      const res = await getEnterpriseAllUsers(token, enterprise?.id ?? "");
      if (res instanceof GetEnterpriseAllUsersResponse) {
        const uName = res
          .getUsersList()
          .filter(
            (user) =>
              !user.getUsername()?.includes("dummy_ommuse") &&
              user.getUsername() !== userName
          )
          .map((user) => user.getUsername() || user.getEmail());
        setUserNames(uName);
        setUsersList(
          res
            .getUsersList()
            .filter(
              (user) =>
                !user.getUsername()?.includes("dummy_ommuse") &&
                user.getUsername() !== userName
            )
        );
      }
    };
    if (open) {
      handleUserNames();
    }
  }, [enterprise?.id, getValidAccessToken, userName, open]);

  return (
    <Modal
      isOpen={open}
      toggle={onClose}
      style={{ marginTop: 100 }}
    >
      <ModalBody style={{ backgroundColor: APP_BACKGROUND_COLOR }}>
        <Box sx={shareModalStyles.shareIconContainer}>
          <img
            style={shareModalStyles.shareIcons}
            src="/assets/icons/TracksPageIcons/users-plus.svg"
            alt="icon"
          />
        </Box>
        <Typography
          sx={{ color: TEXT_COLOR, marginBottom: "20px" }}
          variant="h6"
          fontFamily={FONT_FAMILY}
        >
          Edit {selectedFolderIds.size} Folder{selectedFolderIds.size !== 1 ? "s" : ""}
        </Typography>
        <IconButton
          onClick={onClose}
          style={styles.postMessageModalIcon}
        >
          <CloseIcon style={{ color: APP_COLOR }} />
        </IconButton>

        {/* Privacy Section */}
        <Box
          sx={{
            border: `1px solid ${BORDER_COLOR}`,
            borderRadius: "8px",
            padding: "12px 16px",
            marginBottom: "20px",
          }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={privacyEnabled}
                onChange={(e) => setPrivacyEnabled(e.target.checked)}
              />
            }
            label={
              <Typography sx={{ color: TEXT_COLOR, fontFamily: FONT_FAMILY, fontSize: "14px" }}>
                Change Privacy Setting
              </Typography>
            }
          />
          {privacyEnabled && (
            <Box sx={{ display: "flex", gap: "10px", marginTop: "10px" }}>
              <CustomButton
                text="Make Private"
                onClick={() => setMakePrivate(true)}
                customStyles={{
                  width: "48%",
                  minWidth: "120px",
                  opacity: makePrivate ? 1 : 0.5,
                }}
              />
              <CustomButton
                text="Make Public"
                onClick={() => setMakePrivate(false)}
                customStyles={{
                  width: "48%",
                  minWidth: "120px",
                  opacity: !makePrivate ? 1 : 0.5,
                }}
              />
            </Box>
          )}
        </Box>

        {/* Collaborators Section */}
        <Typography
          sx={{ color: TEXT_COLOR, marginBottom: "10px", fontFamily: FONT_FAMILY, fontSize: "14px" }}
        >
          Add Collaborators
        </Typography>
        <Box sx={collaboratorsModalStyles.userNameLabelContainer}>
          <Box sx={{ width: "80%" }}>
            <label
              htmlFor="newCollaboratorUser"
              style={styles.modalCreatorUsername}
            >
              {"Collaborator's Username"}
            </label>
            <UsernameSelectField
              freeSolo
              forcePopupIcon={false}
              isOptionEqualToValue={(option, value) => option === value}
              inputValue={collabVal}
              onInputChange={(
                event: React.SyntheticEvent<Element, Event>,
                value: string
              ) => {
                setCollabVal(value.trim());
              }}
              options={userNames.filter(
                (name) => !collaborators.some((c) => c.getUsername() === name)
              )}
              renderInput={(params) => (
                <TextField
                  color="primary"
                  style={{
                    backgroundColor: APP_BACKGROUND_COLOR,
                    color: TEXT_COLOR,
                  }}
                  {...params}
                  label=" "
                  InputLabelProps={{
                    shrink: false,
                  }}
                  classes={{ root: classes.customTextField }}
                />
              )}
              renderOption={(props, option: any) => (
                <li {...props} style={{ fontFamily: FONT_FAMILY }}>
                  {option}
                </li>
              )}
            />
          </Box>
          <CustomButton
            isDisabled={collabVal === ""}
            text="Add"
            onClick={() => addCollaborator(collabVal)}
            customStyles={{
              ...collaboratorsModalStyles.customButtonStyles,
              color: "black",
              "&.Mui-disabled": { color: "black", opacity: 0.4 },
            }}
          />
        </Box>

        <Box style={collaboratorsModalStyles.collaboratorContainer}>
          <Box>
            {collaborators &&
              collaborators.length > 0 &&
              collaborators.map((collaborator, index) => (
                <Box
                  className="collaborator"
                  style={{ width: "100%" }}
                  key={index}
                >
                  <img
                    style={collaboratorsModalStyles.profilePic}
                    src={
                      collaborator.getProfilePicture()
                        ? collaborator.getProfilePicture()
                        : enterprise?.image
                    }
                    alt={collaborator.getUsername()}
                  />
                  <p style={{ fontFamily: FONT_FAMILY, flexGrow: 1, color: TEXT_COLOR }}>
                    {collaborator.getUsername()}
                  </p>
                  <IconButton onClick={() => removeCollaborator(collaborator)}>
                    <DeleteForeverIcon />
                  </IconButton>
                </Box>
              ))}
          </Box>
        </Box>

        <CustomButton
          text="Confirm"
          loading={loading}
          onClick={handleConfirm}
          isDisabled={collaborators.length === 0 && !privacyEnabled}
          customStyles={{ width: "100%", color: "black", "&.Mui-disabled": { color: "black", opacity: 0.4 } }}
        />
      </ModalBody>
    </Modal>
  );
};

export default BatchEditFolderModal;
